// WarrantyTab — full claim management view for a deal.
//
// Shows all warranty_claims for the deal with photo grids, event timelines,
// comment boxes, and status controls. Writers (Jennifer, Robert, admin) can
// change status, log inspections, and resolve claims. Read-only for everyone
// else (RMs see their deal's warranty work for awareness).
//
// Data flow:
// - Claims realtime-subscribed via supabase channel — UI updates as Jennifer
//   marks status from her queue, etc.
// - Photos + files query deal_documents WHERE warranty_claim_id matches
//   (file_type warranty_photo | warranty_document). Anyone who can open the
//   deal can add them — see lib/warrantyAttachments + migration
//   20260915_warranty_attachments (Sep 15, 2026).
// - Events query warranty_claim_events WHERE claim_id matches
// - Status changes write warranty_claims directly; the BEFORE UPDATE trigger
//   fires status_changed / resolution / inspection_logged events automatically
// - Comments write warranty_claim_events directly (RLS allows for writers)
//
// Notification side-effects fire via notifyOnWarrantyClaim helpers, never
// blocking the UI.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import {
  notifyWarrantyPhotoAdded,
  notifyWarrantyStatusChanged,
} from "../../lib/notifyOnWarrantyClaim";
import OpenWarrantyClaimModal from "./OpenWarrantyClaimModal";
import { fmtSupabaseError } from "../../lib/fmtError";
import AttachmentPicker from "../shared/AttachmentPicker";
import {
  WARRANTY_ATTACHMENT_FILE_TYPES,
  describeAttachmentCounts,
  describeFailures,
  fmtBytes,
  isImageMime,
  uploadStaffWarrantyAttachments,
} from "../../lib/warrantyAttachments";

interface DealForTab {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  owner_id: string | null;
  stage: string | null;
  warranty_registered_date: string | null;
  warranty_type: string | null;
  oc_warranty_number: string | null;
  warranty_expiration_date: string | null;
}

interface ClaimRow {
  id: string;
  deal_id: string;
  title: string | null;
  category: string | null;
  reported_issue: string | null;
  priority: string | null;
  status: string;
  opened_via: string | null;
  opened_by: string | null;
  claim_date: string | null;
  assigned_to: string | null;
  inspection_date: string | null;
  inspection_notes: string | null;
  resolution: string | null;
  resolution_date: string | null;
  resolution_notes: string | null;
  cost: number | null;
  covered_by_manufacturer: boolean | null;
  manufacturer_claim_number: string | null;
  created_at: string;
  updated_at: string;
}

interface AttachmentRow {
  id: string;
  warranty_claim_id: string;
  storage_path: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

interface EventRow {
  id: string;
  claim_id: string;
  kind: string;
  actor_id: string | null;
  actor_label: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

interface RosterLite { id: string; full_name: string }

const STATUS_LABELS: Record<string, string> = {
  claim_filed: "Filed",
  in_progress: "In progress",
  claim_approved: "Approved",
  claim_denied: "Denied",
  voided: "Voided",
  expired: "Expired",
  pending_registration: "Pending registration",
  registered: "Registered",
  active: "Active",
};
const STATUS_STYLES: Record<string, string> = {
  claim_filed: "bg-amber-100 text-amber-700 border-amber-200",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  claim_approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  claim_denied: "bg-slate-200 text-slate-700 border-slate-300",
  voided: "bg-slate-200 text-slate-700 border-slate-300",
  expired: "bg-slate-200 text-slate-700 border-slate-300",
};

const RESOLUTION_OPTIONS = [
  { value: "repair_completed", label: "Repair completed by Bardin" },
  { value: "manufacturer_replaced", label: "Manufacturer replaced" },
  { value: "no_action_needed", label: "No action needed" },
  { value: "declined_not_covered", label: "Declined — not covered" },
  { value: "declined_out_of_warranty", label: "Declined — out of warranty" },
  { value: "other", label: "Other" },
];

const ACTIVE = new Set(["claim_filed", "in_progress"]);

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function WarrantyTab({ deal, reload }: { deal: DealForTab; reload: () => void | Promise<void> }) {
  const { member, isSuperUser } = useAuth();
  const [claims, setClaims] = useState<ClaimRow[] | null>(null);
  const [attachmentsByClaim, setAttachmentsByClaim] = useState<Record<string, AttachmentRow[]>>({});
  const [eventsByClaim, setEventsByClaim] = useState<Record<string, EventRow[]>>({});
  const [roster, setRoster] = useState<Record<string, RosterLite>>({});
  const [canWrite, setCanWrite] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Resolve writer status from finance_settings + admin role
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!member) return;
      // Super-users resolve to admin server-side (get_my_role()), so
      // warranty_can_write() already admits them — mirror that here so the
      // status controls don't hide for Chad/Jeremy/Jennifer-as-super.
      const isAdmin = member.role === "admin" || isSuperUser;
      if (isAdmin) {
        if (!cancelled) setCanWrite(true);
        return;
      }
      const { data } = await supabase
        .from("finance_settings")
        .select("value_jsonb")
        .eq("key", "warranty_writers")
        .maybeSingle();
      const emails = Array.isArray(data?.value_jsonb) ? (data!.value_jsonb as string[]) : [];
      if (!cancelled) setCanWrite(emails.includes(member.email));
    })();
    return () => { cancelled = true; };
  }, [member, isSuperUser]);

  // Initial + refresh load
  async function loadAll() {
    const { data: claimsData, error: claimsErr } = await supabase
      .from("warranty_claims")
      .select("*")
      .eq("deal_id", deal.id)
      .order("updated_at", { ascending: false });
    if (claimsErr) {
      setErr(claimsErr.message);
      return;
    }
    const cs = (claimsData ?? []) as ClaimRow[];
    setClaims(cs);
    const claimIds = cs.map((c) => c.id);
    if (claimIds.length === 0) {
      setAttachmentsByClaim({});
      setEventsByClaim({});
      return;
    }

    const [{ data: photoData }, { data: eventData }] = await Promise.all([
      supabase
        .from("deal_documents")
        .select("id, warranty_claim_id, storage_path, file_name, file_type, file_size, mime_type, uploaded_by, created_at")
        .in("warranty_claim_id", claimIds)
        .in("file_type", WARRANTY_ATTACHMENT_FILE_TYPES)
        .is("superseded_at", null)
        .order("created_at", { ascending: false }),
      supabase
        .from("warranty_claim_events")
        .select("id, claim_id, kind, actor_id, actor_label, payload, created_at")
        .in("claim_id", claimIds)
        .order("created_at", { ascending: false }),
    ]);

    const ph: Record<string, AttachmentRow[]> = {};
    for (const p of (photoData ?? []) as AttachmentRow[]) {
      (ph[p.warranty_claim_id] ??= []).push(p);
    }
    setAttachmentsByClaim(ph);

    const ev: Record<string, EventRow[]> = {};
    for (const e of (eventData ?? []) as EventRow[]) {
      (ev[e.claim_id] ??= []).push(e);
    }
    setEventsByClaim(ev);

    // Resolve roster names referenced by assigned_to/opened_by/actor_id
    const userIds = new Set<string>();
    for (const c of cs) {
      if (c.assigned_to) userIds.add(c.assigned_to);
      if (c.opened_by) userIds.add(c.opened_by);
    }
    for (const list of Object.values(ev)) {
      for (const e of list) if (e.actor_id) userIds.add(e.actor_id);
    }
    for (const list of Object.values(ph)) {
      for (const p of list) if (p.uploaded_by) userIds.add(p.uploaded_by);
    }
    if (userIds.size > 0) {
      const { data: rosterRows } = await supabase
        .from("team_roster")
        .select("id, full_name")
        .in("id", Array.from(userIds));
      const m: Record<string, RosterLite> = {};
      for (const r of (rosterRows ?? []) as RosterLite[]) m[r.id] = r;
      setRoster(m);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  // Realtime — claims, events, and photos all reload when something changes
  useEffect(() => {
    const channel = supabase
      .channel(`warranty_tab_${deal.id}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "warranty_claims", filter: `deal_id=eq.${deal.id}` },
        () => { loadAll(); })
      .on("postgres_changes",
        { event: "*", schema: "public", table: "warranty_claim_events" },
        (payload) => {
          // Filter client-side to events for our claims
          const claimId = (payload.new as { claim_id?: string } | null)?.claim_id ?? (payload.old as { claim_id?: string } | null)?.claim_id;
          if (!claimId) return;
          if (claims?.some((c) => c.id === claimId)) loadAll();
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, claims?.map((c) => c.id).join(",")]);

  const counts = useMemo(() => {
    if (!claims) return { active: 0, resolved: 0, declined: 0, total: 0 };
    let active = 0, resolved = 0, declined = 0;
    for (const c of claims) {
      if (ACTIVE.has(c.status)) active += 1;
      else if (c.status === "claim_approved") resolved += 1;
      else if (c.status === "claim_denied" || c.status === "voided" || c.status === "expired") declined += 1;
    }
    return { active, resolved, declined, total: claims.length };
  }, [claims]);

  if (err) return <div className="p-4 text-sm text-red-600">Couldn't load warranty — {err}</div>;
  if (!claims) return <div className="p-4 text-sm text-slate-500">Loading warranty…</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Warranty</div>
            <div className="text-sm text-slate-700 mt-0.5">
              {deal.warranty_type || "Owens Corning Preferred Protection"}
              {deal.oc_warranty_number ? ` · #${deal.oc_warranty_number}` : ""}
            </div>
            {deal.warranty_registered_date && (
              <div className="text-xs text-slate-500 mt-0.5">
                Registered {deal.warranty_registered_date}
                {deal.warranty_expiration_date ? ` · expires ${deal.warranty_expiration_date}` : ""}
              </div>
            )}
          </div>
          {canWrite && (
            <button onClick={() => setOpenModal(true)} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90">
              + Open new claim
            </button>
          )}
        </div>
        <div className="mt-3 flex gap-2 text-xs">
          <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">{counts.active} active</span>
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">{counts.resolved} approved</span>
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{counts.declined} declined/void</span>
        </div>
      </div>

      {/* Claims list */}
      {claims.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
          No warranty claims have been filed on this deal.
          {canWrite && (
            <>
              <br />
              <button onClick={() => setOpenModal(true)} className="mt-3 px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90">
                Open the first claim
              </button>
            </>
          )}
        </div>
      ) : (
        claims.map((claim) => (
          <ClaimCard
            key={claim.id}
            deal={deal}
            claim={claim}
            attachments={attachmentsByClaim[claim.id] ?? []}
            events={eventsByClaim[claim.id] ?? []}
            roster={roster}
            canWrite={canWrite}
            onChanged={loadAll}
          />
        ))
      )}

      {openModal && (
        <OpenWarrantyClaimModal
          deal={deal}
          onClose={() => setOpenModal(false)}
          onCreated={async () => {
            setOpenModal(false);
            await loadAll();
            await reload();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClaimCard — per-claim card with photos, events, comments, controls
// ---------------------------------------------------------------------------

function ClaimCard({
  deal, claim, attachments, events, roster, canWrite, onChanged,
}: {
  deal: DealForTab;
  claim: ClaimRow;
  attachments: AttachmentRow[];
  events: EventRow[];
  roster: Record<string, RosterLite>;
  canWrite: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const { member, isSuperUser } = useAuth();
  const [resolveOpen, setResolveOpen] = useState<null | "claim_approved" | "claim_denied" | "voided">(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [commentBody, setCommentBody] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadMsg, setUploadMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  const dealForNotify = {
    id: deal.id,
    homeowner_name: deal.homeowner_name,
    property_street: deal.property_street,
    property_city: deal.property_city,
    owner_id: deal.owner_id,
  };
  const claimSummary = { id: claim.id, title: claim.title, category: claim.category, status: claim.status, opened_via: claim.opened_via };

  async function changeStatus(next: "in_progress" | "claim_approved" | "claim_denied" | "voided", extra: Partial<ClaimRow> = {}) {
    setBusy(next);
    try {
      const fromStatus = claim.status;
      const update: Partial<ClaimRow> = { status: next, ...extra };
      const { error } = await supabase.from("warranty_claims").update(update).eq("id", claim.id);
      if (error) throw error;
      // Status_changed event auto-logged by trigger. Notify writers + RM.
      notifyWarrantyStatusChanged(dealForNotify, claimSummary, fromStatus, next);
      await onChanged();
    } catch (e) {
      alert("Couldn't update status: " + fmtSupabaseError(e));
    } finally {
      setBusy(null);
    }
  }

  // Revert a claim back to an earlier stage — admins/super-users only. Used to
  // fix a mistaken resolution or a premature stage change. Reverting OUT of a
  // terminal status also clears the recorded resolution so the claim is genuinely
  // active again (the BEFORE-UPDATE trigger only stamps resolution_date going
  // INTO a terminal status; it never clears on the way back, so we do it here).
  async function revertStatus(next: "claim_filed" | "in_progress") {
    setBusy("revert");
    try {
      const fromStatus = claim.status;
      const wasTerminal = !ACTIVE.has(fromStatus);
      const update: Partial<ClaimRow> = { status: next };
      if (wasTerminal) {
        update.resolution = null;
        update.resolution_date = null;
        update.resolution_notes = null;
        update.cost = null;
        update.manufacturer_claim_number = null;
        update.covered_by_manufacturer = null;
      }
      const { error } = await supabase.from("warranty_claims").update(update).eq("id", claim.id);
      if (error) throw error;
      // status_changed event auto-logged by trigger (records from → to for the timeline).
      notifyWarrantyStatusChanged(dealForNotify, claimSummary, fromStatus, next);
      await onChanged();
    } catch (e) {
      alert("Couldn't revert status: " + fmtSupabaseError(e));
    } finally {
      setBusy(null);
    }
  }

  async function logInspection(date: string, notes: string) {
    setBusy("inspection");
    try {
      const { error } = await supabase
        .from("warranty_claims")
        .update({ inspection_date: date, inspection_notes: notes })
        .eq("id", claim.id);
      if (error) throw error;
      await onChanged();
    } catch (e) {
      alert("Couldn't log inspection: " + fmtSupabaseError(e));
    } finally {
      setBusy(null);
    }
  }

  async function uploadAttachments(files: File[]) {
    if (files.length === 0) return;
    setBusy("upload");
    setUploadMsg(null);
    setUploadProgress({ done: 0, total: files.length });
    try {
      const result = await uploadStaffWarrantyAttachments({
        dealId: deal.id,
        claimId: claim.id,
        files,
        uploaderId: member?.id ?? null,
        uploaderLabel: member?.full_name ?? null,
        onProgress: (done, total) => setUploadProgress({ done, total }),
      });
      const added = describeAttachmentCounts(result.photoCount, result.documentCount);
      if (result.uploaded.length > 0) {
        notifyWarrantyPhotoAdded(dealForNotify, claimSummary, result.uploaded.length, added);
      }
      if (result.failures.length > 0) {
        const n = result.failures.length;
        setUploadMsg({
          tone: "err",
          text:
            (result.uploaded.length > 0 ? `Added ${added}. ` : "") +
            `Couldn't add ${n === 1 ? "1 file" : `${n} files`}: ${describeFailures(result.failures)}`,
        });
      } else {
        setUploadMsg({ tone: "ok", text: `Added ${added}.` });
      }
      await onChanged();
    } catch (e) {
      setUploadMsg({ tone: "err", text: "Upload failed: " + fmtSupabaseError(e) });
    } finally {
      setBusy(null);
      setUploadProgress(null);
    }
  }

  async function postComment() {
    if (!commentBody.trim() || !member?.id) return;
    setCommentBusy(true);
    try {
      const { error } = await supabase.from("warranty_claim_events").insert({
        claim_id: claim.id,
        kind: "comment",
        actor_id: member.id,
        actor_label: member.full_name || "Staff",
        payload: { body: commentBody.trim() },
      });
      if (error) throw error;
      setCommentBody("");
      await onChanged();
    } catch (e) {
      alert("Couldn't post comment: " + fmtSupabaseError(e));
    } finally {
      setCommentBusy(false);
    }
  }

  const isActive = ACTIVE.has(claim.status);
  const statusLabel = STATUS_LABELS[claim.status] || claim.status;
  const statusStyle = STATUS_STYLES[claim.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const openedByName = claim.opened_via === "homeowner_portal" ? "Homeowner" : (claim.opened_by ? roster[claim.opened_by]?.full_name : "Staff") || "Staff";
  const assignedName = claim.assigned_to ? roster[claim.assigned_to]?.full_name ?? "—" : "—";

  return (
    <div className={"bg-white border rounded-lg overflow-hidden " + (isActive ? "border-amber-200" : "border-slate-200")}>
      {/* Header */}
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={"text-xs px-2 py-0.5 rounded border " + statusStyle}>{statusLabel}</span>
              {claim.category && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{claim.category.replace(/_/g, " ")}</span>}
              {claim.priority === "high" && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">high priority</span>}
            </div>
            <div className="text-sm font-semibold text-bardin-ink mt-1.5">{claim.title || claim.reported_issue || "(no title)"}</div>
            {claim.reported_issue && claim.reported_issue !== claim.title && (
              <div className="text-xs text-slate-600 mt-1">{claim.reported_issue}</div>
            )}
            <div className="text-xs text-slate-500 mt-2">
              Filed {claim.claim_date} by {openedByName} · Assigned to {assignedName}
            </div>
          </div>
        </div>
      </div>

      {/* Photos & files — anyone who can open the deal can add them (RLS on
          deal_documents + the photo_added event policy enforce it server-side).
          Renders on every claim regardless of status, so an OC approval letter
          or a follow-up photo can still land on a resolved claim. */}
      <AttachmentsSection
        attachments={attachments}
        roster={roster}
        busy={busy === "upload"}
        progress={uploadProgress}
        message={uploadMsg}
        onFiles={uploadAttachments}
      />

      {/* Resolution (when terminal) */}
      {!isActive && claim.resolution && (
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="text-xs font-medium text-slate-700">Resolution</div>
          <div className="text-sm text-slate-700 mt-0.5">
            {RESOLUTION_OPTIONS.find((r) => r.value === claim.resolution)?.label || claim.resolution}
            {claim.resolution_date ? ` · ${claim.resolution_date}` : ""}
            {claim.cost ? ` · $${claim.cost.toFixed(2)}` : ""}
          </div>
          {claim.resolution_notes && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{claim.resolution_notes}</div>}
        </div>
      )}

      {/* Inspection */}
      {claim.inspection_date && (
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-xs font-medium text-slate-700">Inspection — {claim.inspection_date}</div>
          {claim.inspection_notes && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{claim.inspection_notes}</div>}
        </div>
      )}

      {/* Status controls (writers, only when active) */}
      {canWrite && isActive && (
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap gap-2">
          {claim.status === "claim_filed" && (
            <button
              onClick={() => changeStatus("in_progress")}
              disabled={busy === "in_progress"}
              className="px-2.5 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy === "in_progress" ? "Marking…" : "Mark in progress"}
            </button>
          )}
          <button
            onClick={() => setResolveOpen("claim_approved")}
            className="px-2.5 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Resolve as approved
          </button>
          <button
            onClick={() => setResolveOpen("claim_denied")}
            className="px-2.5 py-1 text-xs font-medium rounded bg-slate-700 text-white hover:bg-slate-800"
          >
            Decline
          </button>
          <button
            onClick={() => setResolveOpen("voided")}
            className="px-2.5 py-1 text-xs font-medium rounded bg-slate-500 text-white hover:bg-slate-600"
          >
            Void
          </button>
          <InspectionLogger onSubmit={logInspection} busy={busy === "inspection"} />
        </div>
      )}

      {/* Revert status (admins / super-users only) — fix a mistaken resolution
          or stage. Renders whenever the claim is past "Filed" so there's an
          earlier stage to go back to. */}
      {isSuperUser && claim.status !== "claim_filed" && (
        <RevertStatusControl
          status={claim.status}
          busy={busy === "revert"}
          onRevert={revertStatus}
        />
      )}

      {/* Comment box (writers) */}
      {canWrite && (
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-xs font-medium text-slate-700 mb-1">Add a comment</div>
          <div className="flex gap-2">
            <input
              type="text"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Note for the team…"
              className="flex-1 px-2.5 py-1 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
              onKeyDown={(e) => { if (e.key === "Enter" && !commentBusy) postComment(); }}
            />
            <button
              onClick={postComment}
              disabled={!commentBody.trim() || commentBusy}
              className="px-3 py-1 text-xs font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
            >
              {commentBusy ? "…" : "Post"}
            </button>
          </div>
        </div>
      )}

      {/* Event timeline */}
      <div className="px-4 py-3 bg-slate-50/50">
        <div className="text-xs font-medium text-slate-700 mb-2">Timeline</div>
        <ul className="space-y-1.5">
          {events.map((e) => <EventRow key={e.id} event={e} />)}
          {events.length === 0 && <li className="text-xs text-slate-400">No events yet.</li>}
        </ul>
      </div>

      {/* Resolve modal */}
      {resolveOpen && (
        <ResolveModal
          targetStatus={resolveOpen}
          onClose={() => setResolveOpen(null)}
          onResolve={async (resolution, notes, cost, mfgNumber, mfgCovered) => {
            await changeStatus(resolveOpen, {
              resolution,
              resolution_notes: notes || null,
              resolution_date: new Date().toISOString().slice(0, 10),
              cost: cost,
              manufacturer_claim_number: mfgNumber || null,
              covered_by_manufacturer: mfgCovered,
            });
            setResolveOpen(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function PhotoThumb({ photo }: { photo: AttachmentRow }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage.from(attachmentBucket(photo.storage_path)).createSignedUrl(photo.storage_path, 3600);
      if (!cancelled) setUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [photo.storage_path]);

  function openFullSize() {
    if (!url) return;
    window.open(url, "_blank", "noopener");
  }

  return (
    <button
      onClick={openFullSize}
      className="aspect-square overflow-hidden rounded border border-slate-200 bg-slate-100 hover:border-slate-400 transition"
      title={photo.file_name}
    >
      {url ? (
        <img src={url} alt={photo.file_name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">…</div>
      )}
    </button>
  );
}

function EventRow({ event }: { event: EventRow }) {
  const icon = event.kind === "comment" ? "💬"
    : event.kind === "opened" ? "🛡️"
    : event.kind === "status_changed" ? "→"
    : event.kind === "resolution" ? "✅"
    : event.kind === "photo_added" ? "📸"
    : event.kind === "assigned" ? "👤"
    : event.kind === "inspection_logged" ? "🔍"
    : "•";
  const actor = event.actor_label || "System";
  const detail = (() => {
    const p = event.payload || {};
    if (event.kind === "comment") return (p as { body?: string }).body || "";
    if (event.kind === "status_changed") return `${(p as { from?: string }).from} → ${(p as { to?: string }).to}`;
    if (event.kind === "opened") return `${(p as { opened_via?: string }).opened_via === "homeowner_portal" ? "Filed by homeowner via portal" : "Filed by staff"}${(p as { category?: string }).category ? " · " + (p as { category?: string }).category : ""}`;
    if (event.kind === "resolution") return (p as { resolution?: string }).resolution || "";
    if (event.kind === "photo_added") {
      const q = p as { file_name?: string; file_type?: string };
      return q.file_name || (q.file_type === "warranty_document" ? "File added" : "Photo added");
    }
    if (event.kind === "inspection_logged") return `Inspection on ${(p as { inspection_date?: string }).inspection_date}`;
    return event.kind.replace(/_/g, " ");
  })();
  return (
    <li className="text-xs flex items-start gap-2">
      <span className="w-4 flex-shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="font-medium text-slate-700">{actor}</span>
        {detail ? <span className="text-slate-600"> · {detail}</span> : null}
      </span>
      <span className="text-slate-400 flex-shrink-0">{relativeTime(event.created_at)}</span>
    </li>
  );
}

function InspectionLogger({ onSubmit, busy }: { onSubmit: (date: string, notes: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  if (!open) return (
    <button onClick={() => setOpen(true)} className="px-2.5 py-1 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300">
      Log inspection
    </button>
  );
  return (
    <div className="w-full bg-slate-50 border border-slate-200 rounded p-2 flex flex-col gap-2">
      <div className="flex gap-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="px-2 py-1 text-xs rounded border border-slate-300" />
        <input type="text" placeholder="Findings…" value={notes} onChange={(e) => setNotes(e.target.value)} className="flex-1 px-2 py-1 text-xs rounded border border-slate-300" />
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={() => setOpen(false)} className="px-2 py-1 text-xs rounded bg-slate-200 text-slate-700">Cancel</button>
        <button onClick={() => { onSubmit(date, notes); setOpen(false); setNotes(""); }} disabled={busy} className="px-2 py-1 text-xs rounded bg-bardin-teal text-white disabled:opacity-50">
          {busy ? "Saving…" : "Save inspection"}
        </button>
      </div>
    </div>
  );
}

// Staff uploads live under deal-documents/warranty/…; homeowner uploads land
// in homeowner-uploads/{deal}/warranty/… (the edge function records the same
// path). Pick the bucket from the path so both render.
function attachmentBucket(storagePath: string): "deal-documents" | "homeowner-uploads" {
  return storagePath.startsWith("warranty/") ? "deal-documents" : "homeowner-uploads";
}

function isPhotoAttachment(a: AttachmentRow): boolean {
  return a.file_type !== "warranty_document" && (a.mime_type == null || isImageMime(a.mime_type));
}

function docIcon(mime: string | null, name: string): string {
  const m = (mime || "").toLowerCase();
  const n = name.toLowerCase();
  if (m.includes("pdf") || n.endsWith(".pdf")) return "📄";
  if (m.includes("word") || n.endsWith(".doc") || n.endsWith(".docx")) return "📝";
  if (m.includes("excel") || m.includes("spreadsheet") || m === "text/csv" || n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".csv")) return "📊";
  if (m.startsWith("image/")) return "🖼️";
  return "📎";
}

function AttachmentsSection({
  attachments, roster, busy, progress, message, onFiles,
}: {
  attachments: AttachmentRow[];
  roster: Record<string, RosterLite>;
  busy: boolean;
  progress: { done: number; total: number } | null;
  message: { tone: "ok" | "err"; text: string } | null;
  onFiles: (files: File[]) => void;
}) {
  const photos = attachments.filter(isPhotoAttachment);
  const docs = attachments.filter((a) => !isPhotoAttachment(a));
  return (
    <div className="px-4 py-3 border-b border-slate-100">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-medium text-slate-700">Photos &amp; files ({attachments.length})</div>
        {busy && progress && (
          <div className="text-xs text-slate-500">
            Uploading {Math.min(progress.done + 1, progress.total)} of {progress.total}…
          </div>
        )}
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
          {photos.map((p) => <PhotoThumb key={p.id} photo={p} />)}
        </div>
      )}
      {docs.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded mb-2">
          {docs.map((d) => (
            <DocumentRow
              key={d.id}
              doc={d}
              uploaderName={d.uploaded_by ? roster[d.uploaded_by]?.full_name ?? null : "Homeowner"}
            />
          ))}
        </ul>
      )}
      {attachments.length === 0 && <div className="text-xs text-slate-400 mb-2">No photos or files yet.</div>}
      <AttachmentPicker onFiles={(files) => onFiles(files)} disabled={busy} />
      {message && (
        <div
          className={
            "mt-2 text-xs rounded px-2 py-1.5 border " +
            (message.tone === "ok"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-red-50 text-red-700 border-red-200")
          }
        >
          {message.text}
        </div>
      )}
    </div>
  );
}

function DocumentRow({ doc, uploaderName }: { doc: AttachmentRow; uploaderName: string | null }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage.from(attachmentBucket(doc.storage_path)).createSignedUrl(doc.storage_path, 3600);
      if (!cancelled) setUrl(data?.signedUrl ?? null);
    })();
    return () => { cancelled = true; };
  }, [doc.storage_path]);
  const meta = [fmtBytes(doc.file_size), uploaderName, relativeTime(doc.created_at)].filter(Boolean).join(" · ");
  return (
    <li>
      <button
        type="button"
        onClick={() => { if (url) window.open(url, "_blank", "noopener"); }}
        disabled={!url}
        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50 disabled:opacity-60"
        title={doc.file_name}
      >
        <span className="text-base flex-shrink-0" aria-hidden>{docIcon(doc.mime_type, doc.file_name)}</span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-medium text-slate-700 truncate">{doc.file_name}</span>
          {meta && <span className="block text-[11px] text-slate-500 truncate">{meta}</span>}
        </span>
        <span className="text-[11px] text-slate-400 flex-shrink-0">{url ? "Open ↗" : "…"}</span>
      </button>
    </li>
  );
}

// RevertStatusControl — admin/super-user-only "undo" for a warranty claim.
// Collapsed to a single ↩ button; expands to a confirm strip. Terminal claims
// (approved/denied/voided/expired) reopen to Filed or In progress; an active
// "In progress" claim can drop back to Filed. The choice of targets comes from
// the claim's current status.
function RevertStatusControl({
  status,
  busy,
  onRevert,
}: {
  status: string;
  busy: boolean;
  onRevert: (next: "claim_filed" | "in_progress") => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const isTerminal = !ACTIVE.has(status);
  const fromLabel = STATUS_LABELS[status] || status;
  // In progress → only Filed. Terminal → Filed or In progress.
  const targets: Array<"claim_filed" | "in_progress"> = isTerminal
    ? ["in_progress", "claim_filed"]
    : ["claim_filed"];

  if (!open) {
    return (
      <div className="px-4 py-3 border-b border-slate-100">
        <button
          onClick={() => setOpen(true)}
          className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          ↩ Revert status
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/60">
      <div className="text-xs font-medium text-slate-700">
        Revert this claim from <span className="font-semibold">{fromLabel}</span> back to:
      </div>
      {isTerminal && (
        <div className="text-xs text-slate-500 mt-0.5">
          The recorded resolution (notes, cost, OC claim #) will be cleared.
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        {targets.map((t) => (
          <button
            key={t}
            onClick={async () => { await onRevert(t); setOpen(false); }}
            disabled={busy}
            className="px-2.5 py-1 text-xs font-medium rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busy ? "Reverting…" : `→ ${STATUS_LABELS[t] || t}`}
          </button>
        ))}
        <button
          onClick={() => setOpen(false)}
          disabled={busy}
          className="px-2.5 py-1 text-xs font-medium rounded bg-slate-200 text-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ResolveModal({
  targetStatus, onClose, onResolve,
}: {
  targetStatus: "claim_approved" | "claim_denied" | "voided";
  onClose: () => void;
  onResolve: (resolution: string, notes: string, cost: number | null, mfgNumber: string, mfgCovered: boolean) => void | Promise<void>;
}) {
  const isApproved = targetStatus === "claim_approved";
  const isDenied = targetStatus === "claim_denied";
  const allowedResolutions = isApproved
    ? ["repair_completed", "manufacturer_replaced", "no_action_needed", "other"]
    : isDenied
    ? ["declined_not_covered", "declined_out_of_warranty", "other"]
    : ["other"];
  const [resolution, setResolution] = useState<string>(allowedResolutions[0]);
  const [notes, setNotes] = useState("");
  const [cost, setCost] = useState<string>("");
  const [mfgNumber, setMfgNumber] = useState("");
  const [mfgCovered, setMfgCovered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const targetLabel = STATUS_LABELS[targetStatus] || targetStatus;

  async function submit() {
    setSubmitting(true);
    try {
      const costNum = cost.trim() ? parseFloat(cost) : null;
      await onResolve(resolution, notes, isNaN(costNum as number) ? null : costNum, mfgNumber, mfgCovered);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-bardin-ink">{targetLabel}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Resolution</label>
            <select value={resolution} onChange={(e) => setResolution(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300">
              {allowedResolutions.map((v) => {
                const opt = RESOLUTION_OPTIONS.find((o) => o.value === v);
                return <option key={v} value={v}>{opt?.label || v}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 min-h-[80px]" placeholder="What did Bardin do? What did the homeowner agree to?" />
          </div>
          {isApproved && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-700">Cost ($)</label>
                  <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300" placeholder="0.00" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">OC claim #</label>
                  <input type="text" value={mfgNumber} onChange={(e) => setMfgNumber(e.target.value)} className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300" placeholder="(if any)" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={mfgCovered} onChange={(e) => setMfgCovered(e.target.checked)} />
                Manufacturer covered (OC paid)
              </label>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-teal text-white disabled:opacity-50">
            {submitting ? "Saving…" : `Mark ${targetLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}
